import { useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import { useId, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { api, ApiError, unwrap } from '@/api/client'
import { blockerText } from '@/api/problemText'
import {
  nearbyQuery,
  orderQuery,
  queryKeys,
  type OrderDetail,
  type TransitionAction,
} from '@/api/queries'
import { ConfirmActionDialog } from '@/components/ConfirmActionDialog'
import { ActionBar, EmptyState, PageHeader, ReadOnlyFields } from '@/components/page'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { OrderHistory } from '@/features/history/OrderHistory'
import { crewsQuery } from '@/features/orders/crews'
import { RequestedStartDialog } from '@/features/orders/RequestedStart'
import { ActionFields } from '@/features/orders/ActionFields'
import {
  EMPTY_PARAMS,
  paramsComplete,
  transitionExtras,
  type ActionParams,
} from '@/features/orders/actionParams'
import { AmpelBadge } from '@/features/orders/AmpelBadge'
import { OrderFiles } from '@/features/files/OrderFiles'
import { useViewer } from '@/features/identity/useViewer'
import { ClassificationSuggestion } from '@/features/classification/ClassificationSuggestion'
import { BillingSection } from '@/features/billing/BillingSection'
import { PermitSection } from '@/features/permits/PermitSection'
import { OrderFieldwork } from '@/features/review/OrderFieldwork'
import { ampelOf } from '@/features/orders/ampel'
import { requireNav } from '@/nav/guard'

export const Route = createFileRoute('/_app/orders/$orderId')({
  beforeLoad: requireNav('orders'),
  component: OrderDetailPage,
})

/** Aktionen, die sich nicht zurücknehmen lassen: Begründung Pflicht (Plan: Bestätigungsdialog). */
const IRREVERSIBLE: ReadonlySet<TransitionAction> = new Set([
  'CANCEL',
  'REJECT',
  'REPORT_TO_CLIENT',
  'RECORD_CLIENT_ACCEPTANCE',
  'MARK_BILLED',
])

function OrderDetailPage() {
  const { t, i18n } = useTranslation()
  const { orderId } = Route.useParams()
  const queryClient = useQueryClient()
  const { isClient, isSub, can } = useViewer()
  const { data, isPending, error } = useQuery(orderQuery(orderId))
  const [pendingAction, setPendingAction] = useState<TransitionAction | null>(null)
  const [params, setParams] = useState<ActionParams>(EMPTY_PARAMS)
  const [tab, setTab] = useState<'details' | 'history'>('details')
  const crewTargets = useQuery({ ...crewsQuery, enabled: pendingAction !== null })
  const [doneMessage, setDoneMessage] = useState<string | null>(null)
  const [requested, setRequested] = useState<'set' | 'clear' | null>(null)

  const back = (
    <Button asChild variant="link" className="mb-2 px-0">
      <Link to="/orders">{t('order.back')}</Link>
    </Button>
  )

  if (isPending) return <Skeleton className="h-40 w-full" aria-label={t('common.loading')} />
  if (error || !data) {
    const notFound = error instanceof ApiError && error.status === 404
    return (
      <>
        {back}
        <EmptyState
          title={notFound ? t('order.notFoundTitle') : t('order.unavailableTitle')}
          body={notFound ? t('order.notFoundBody') : t('order.unavailableBody')}
        />
      </>
    )
  }

  const { order, etag } = data
  const formatDate = (iso: string) =>
    new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium', timeStyle: 'short' }).format(
      new Date(iso),
    )

  async function runTransition(action: TransitionAction, reason: string | undefined) {
    const result = await api.POST('/api/v1/orders/{id}/transitions', {
      params: {
        path: { id: orderId },
        header: { 'Idempotency-Key': crypto.randomUUID(), 'If-Match': etag ?? '' },
      },
      body: {
        action,
        ...(reason ? { reason } : {}),
        ...transitionExtras(action, params, crewTargets.data),
      },
    })
    const updated: OrderDetail = unwrap(result)
    // Neuen Stand samt neuem ETag sofort übernehmen; Listen neu laden
    queryClient.setQueryData(queryKeys.order(orderId), {
      order: updated,
      etag: result.response.headers.get('ETag'),
    })
    await queryClient.invalidateQueries({ queryKey: queryKeys.orders, refetchType: 'none' })
    setDoneMessage(t('order.done', { action: actionLabel(action) }))
  }

  const canRequestStart =
    can('order.schedule') &&
    !isSub &&
    !['CLIENT_ACCEPTED', 'BILLED', 'CANCELLED'].includes(order.status)
  const area = order.rajon ?? order.municipality ?? order.postalCode ?? order.mdf
  // Ein bereits terminierter Auftrag wird umgeplant (neuer Tag, neue Kolonne)
  const rescheduling = order.status === 'SCHEDULED' || order.status === 'IN_PROGRESS'
  const actionLabel = (action: TransitionAction) =>
    action === 'SCHEDULE' && rescheduling ? t('order.reschedule') : t(`orderAction.${action}`)

  return (
    <>
      {back}
      <PageHeader title={order.number} description={order.address}>
        <Badge className="text-base">{t(`orderStatus.${order.status}`)}</Badge>
      </PageHeader>

      <div role="tablist" aria-label={t('order.tabs')} className="mb-4 flex gap-1 border-b">
        {(['details', 'history'] as const).map((id) => (
          <button
            key={id}
            type="button"
            role="tab"
            id={`order-tab-${id}`}
            aria-selected={tab === id}
            aria-controls={`order-panel-${id}`}
            className={`min-h-11 border-b-2 px-4 text-sm font-medium ${
              tab === id
                ? 'border-primary text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
            onClick={() => setTab(id)}
          >
            {t(`order.tab.${id}`)}
          </button>
        ))}
      </div>

      {tab === 'history' ? (
        <div role="tabpanel" id="order-panel-history" aria-labelledby="order-tab-history">
          <OrderHistory orderId={orderId} />
        </div>
      ) : (
        <div role="tabpanel" id="order-panel-details" aria-labelledby="order-tab-details">
          {/* Lesemodus: Werte nur anzeigen; Änderungen laufen ausschließlich über die Aktionsleiste. */}
          <ReadOnlyFields
            items={[
              { label: t('order.number'), value: order.number },
              { label: t('order.externalRef'), value: order.externalRef ?? t('common.none') },
              { label: t('order.type'), value: order.orderType ?? t('common.none') },
              { label: t('order.status'), value: t(`orderStatus.${order.status}`) },
              { label: t('order.clientStatus'), value: order.clientStatus ?? t('common.none') },
              { label: t('order.area'), value: area ?? order.region ?? t('common.none') },
              {
                label: t('requested.label'),
                value: order.requestedStart ? (
                  <>
                    {formatDate(order.requestedStart)}
                    {order.requestedNote && (
                      <span className="block text-sm font-normal text-muted-foreground">
                        {order.requestedNote}
                      </span>
                    )}
                  </>
                ) : (
                  t('common.none')
                ),
              },
              ...(order.assignedCrew
                ? [
                    {
                      label: t('requested.crew'),
                      value: order.assignedCrew.name ?? t('common.none'),
                    },
                  ]
                : []),
              {
                label: t('order.nextDeadline'),
                value: order.nextDeadlineAt ? (
                  <>
                    <AmpelBadge ampel={ampelOf(order.nextDeadlineAt)} />
                    <span className="block text-sm font-normal text-muted-foreground">
                      {formatDate(order.nextDeadlineAt)}
                    </span>
                  </>
                ) : (
                  t('common.none')
                ),
              },
            ]}
          />

          {canRequestStart && (
            <div className="mt-4 flex flex-wrap gap-2">
              <Button type="button" variant="outline" onClick={() => setRequested('set')}>
                {t('requested.set')}
              </Button>
              {order.requestedStart && (
                <Button type="button" variant="outline" onClick={() => setRequested('clear')}>
                  {t('requested.clear')}
                </Button>
              )}
            </div>
          )}
          {!isClient && <ClassificationSuggestion orderId={orderId} />}

          {/* Lesezugang des Auftraggebers (S-07): Aktionsleiste nur, wenn der Server ihm Aktionen erlaubt (Disposition) */}
          {(!isClient || order.allowedActions.length > 0) && (
            <section aria-labelledby="order-actions" className="mt-6 grid gap-3">
              <h2 id="order-actions" className="text-lg font-medium">
                {t('order.actions')}
              </h2>
              {(order.blockers ?? []).length > 0 && (
                <div className="rounded-md border bg-warning p-3 text-sm text-warning-foreground">
                  <p className="font-medium">{t('order.blockers')}</p>
                  <ul className="list-disc pl-5">
                    {(order.blockers ?? []).map((blocker) => (
                      <li key={`${blocker.code}-${JSON.stringify(blocker.details ?? {})}`}>
                        {blockerText(t, blocker)}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {/* Welche Aktionen erlaubt sind, bestimmt der Server (allowedActions), nicht der Client. */}
              <ActionBar label={t('order.actions')} emptyHint={t('order.noActions')}>
                {order.allowedActions.map((action) => (
                  <Button
                    key={action}
                    type="button"
                    variant={IRREVERSIBLE.has(action) ? 'outline' : 'default'}
                    onClick={() => {
                      setDoneMessage(null)
                      setParams({ ...EMPTY_PARAMS, orderType: order.orderType ?? '' })
                      setPendingAction(action)
                    }}
                  >
                    {actionLabel(action)}
                  </Button>
                ))}
              </ActionBar>
              {doneMessage && (
                <p role="status" className="text-sm">
                  {doneMessage}
                </p>
              )}
            </section>
          )}

          <div className="mt-8 grid gap-8 lg:grid-cols-2">
            <section aria-labelledby="order-steps">
              <h2 id="order-steps" className="mb-2 text-lg font-medium">
                {t('order.stepsTitle')}
              </h2>
              {(order.steps ?? []).length === 0 ? (
                <p className="text-muted-foreground">{t('order.stepsEmpty')}</p>
              ) : (
                <ul className="divide-y rounded-lg border bg-card">
                  {(order.steps ?? []).map((step) => (
                    <li
                      key={step.id}
                      className="flex flex-wrap items-center justify-between gap-2 p-3"
                    >
                      <span className="font-medium">{step.stepType}</span>
                      <Badge variant="secondary">{t(`order.stepStatus.${step.status}`)}</Badge>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <section aria-labelledby="order-deadlines">
              <h2 id="order-deadlines" className="mb-2 text-lg font-medium">
                {t('order.deadlinesTitle')}
              </h2>
              {(order.deadlines ?? []).length === 0 ? (
                <p className="text-muted-foreground">{t('order.deadlinesEmpty')}</p>
              ) : (
                <ul className="divide-y rounded-lg border bg-card">
                  {(order.deadlines ?? []).map((deadline) => (
                    <li key={deadline.id} className="p-3">
                      <p className="font-medium">
                        {deadline.kind} · {t(`order.deadlineState.${deadline.state}`)}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {t('order.actionDue')}: {formatDate(deadline.actionDueAt)}
                      </p>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </div>

          {!isClient && (order.crew ?? []).length > 0 && (
            <section aria-labelledby="order-crew" className="mt-8">
              <h2 id="order-crew" className="mb-2 text-lg font-medium">
                {t('order.crewTitle')}
              </h2>
              <ul className="divide-y rounded-lg border bg-card">
                {(order.crew ?? []).map((member) => (
                  <li key={member.userId} className="p-3">
                    {member.name}
                  </li>
                ))}
              </ul>
            </section>
          )}

          <OrderFieldwork orderId={orderId} hidePersonal={isClient} />

          {can('permit.read') && (
            <PermitSection orderId={orderId} municipality={order.municipality} />
          )}

          <BillingSection orderId={orderId} />

          <OrderFiles orderId={orderId} />

          <NearbySection orderId={orderId} hasLocation={Boolean(order.location)} />
        </div>
      )}

      {requested && (
        <RequestedStartDialog
          order={order}
          etag={etag}
          clear={requested === 'clear'}
          onClose={() => setRequested(null)}
          onDone={setDoneMessage}
        />
      )}

      {pendingAction && (
        <ConfirmActionDialog
          open
          onOpenChange={(open) => !open && setPendingAction(null)}
          actionLabel={actionLabel(pendingAction)}
          irreversible={IRREVERSIBLE.has(pendingAction)}
          confirmDisabled={!paramsComplete(pendingAction, params)}
          onConfirm={(reason) => runTransition(pendingAction, reason)}
        >
          <ActionFields
            action={pendingAction}
            crewOrgId={order.assignedCrew?.orgId ?? order.assignedOrgId ?? undefined}
            params={params}
            onChange={(patch) => setParams((prev) => ({ ...prev, ...patch }))}
          />
        </ConfirmActionDialog>
      )}
    </>
  )
}

const RADII = [500, 1000, 2000] as const

/** Offene Aufträge im Umkreis (PostGIS), nach Entfernung. */
function NearbySection({ orderId, hasLocation }: { orderId: string; hasLocation: boolean }) {
  const { t } = useTranslation()
  const radiusId = useId()
  const [radiusM, setRadiusM] = useState<number>(500)
  const nearby = useQuery({ ...nearbyQuery(orderId, radiusM), enabled: hasLocation })
  return (
    <section aria-labelledby="order-nearby" className="mt-8">
      <div className="mb-2 flex flex-wrap items-end justify-between gap-3">
        <h2 id="order-nearby" className="text-lg font-medium">
          {t('order.nearbyTitle')}
        </h2>
        <div className="grid gap-1">
          <label htmlFor={radiusId} className="text-sm font-medium">
            {t('order.nearbyRadius')}
          </label>
          <select
            id={radiusId}
            className="h-11 rounded-md border border-input bg-card px-3 text-base"
            value={radiusM}
            onChange={(e) => setRadiusM(Number(e.target.value))}
          >
            {RADII.map((r) => (
              <option key={r} value={r}>
                {t('order.distance', { m: r })}
              </option>
            ))}
          </select>
        </div>
      </div>
      {!hasLocation || nearby.data?.length === 0 ? (
        <p className="text-muted-foreground">{t('order.nearbyEmpty')}</p>
      ) : nearby.isError ? (
        <p className="text-destructive">{t('order.nearbyError')}</p>
      ) : nearby.isPending ? (
        <Skeleton className="h-16 w-full" aria-label={t('common.loading')} />
      ) : (
        <ul className="divide-y rounded-lg border bg-card" data-testid="nearby-list">
          {nearby.data.map(({ order, distanceM }) => (
            <li key={order.id} className="flex flex-wrap items-center justify-between gap-2 p-3">
              <div>
                <Link
                  to="/orders/$orderId"
                  params={{ orderId: order.id }}
                  className="inline-flex min-h-11 items-center font-medium text-primary underline"
                >
                  {order.number}
                </Link>
                <p className="text-sm text-muted-foreground">{order.address}</p>
              </div>
              <span className="font-medium">
                {t('order.distance', { m: Math.round(distanceM) })}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}
